import { useMutation } from '@apollo/client'
import { TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_CARRIER } from 'src/graphql/carriers.graphql'
import { isDialableUsNumber, sanitizeEmail } from 'src/lib/contact.utils'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

import type { CarrierDetailRecord } from '../carrier.types'

/**
 * The Carrier Details section, in edit mode.
 *
 * Only this section swaps. The carrier's identity above it and the sections
 * below stay put, so editing a field does not empty the screen around it.
 */
export interface CarrierEditFormProps {
  carrier: CarrierDetailRecord
  onClose: () => void
  /**
   * Re-reads the carrier after a save. The Apollo cache only normalises types
   * with a declared keyFields — Carrier has none — so the mutation result does
   * not merge into the cached query and the screen would keep showing the old
   * values until a reload.
   */
  onSaved: () => void
}

/** Blank an input and the field is cleared, rather than left as it was. */
const orNull = (value: string) => value.trim() || null

/**
 * Judged against the value the form opened with.
 *
 * Carriers hold numbers recorded before this rule existed — extensions, and
 * Mexican numbers the read-only view deliberately keeps visible — and the form
 * submits every field on every save. Refusing one of those would make it
 * impossible to correct the address beside it.
 */
const phoneError = (value: string, initial: string) => {
  const digits = (candidate: string) => candidate.replace(/\D/g, '')
  if (digits(value) === digits(initial)) return null

  return !value.trim() || isDialableUsNumber(value)
    ? null
    : 'Enter a ten-digit US number, or clear the field'
}

/**
 * Numbers are edited the way they are read. Dropping the raw stored value into
 * the input made the same number look like a different one the moment you
 * clicked Edit; the server normalises whatever comes back, so the display form
 * round-trips safely.
 */
const asShown = (phone: string | null | undefined) =>
  formatInternationalPhoneNumber(phone) ?? phone ?? ''

const CarrierEditForm = ({
  carrier,
  onClose,
  onSaved,
}: CarrierEditFormProps) => {
  const form = useForm({
    validateInputOnBlur: true,
    validate: {
      // Caught here as well as on the server, so a typo is flagged next to the
      // field instead of coming back as a toast after a round trip.
      email: (value) =>
        value.trim().toLowerCase() ===
          (carrier.email ?? '').trim().toLowerCase() ||
        !value.trim() ||
        sanitizeEmail(value)
          ? null
          : 'Enter a valid email address, or clear the field',
      phoneNumber: (value) => phoneError(value, asShown(carrier.phoneNumber)),
      faxNumber: (value) => phoneError(value, asShown(carrier.faxNumber)),
    },
    initialValues: {
      dbaName: carrier.dbaName ?? '',
      legalName: carrier.legalName ?? '',
      phoneNumber: asShown(carrier.phoneNumber),
      faxNumber: asShown(carrier.faxNumber),
      email: carrier.email ?? '',
      website: carrier.website ?? '',
      physicalStreet: carrier.physicalStreet ?? '',
      physicalCity: carrier.physicalCity ?? '',
      physicalState: carrier.physicalState ?? '',
      physicalZip: carrier.physicalZip ?? '',
      physicalCountry: carrier.physicalCountry ?? '',
    },
  })

  const [updateCarrier, { loading }] = useMutation(UPDATE_CARRIER, {
    onCompleted: () => {
      toast('Carrier updated', 'success')
      onSaved()
      onClose()
    },
    onError: (error) => toast(error.message, 'error'),
  })

  const submit = form.onSubmit((values) =>
    updateCarrier({
      variables: {
        carrierId: carrier.carrierId,
        input: Object.fromEntries(
          Object.entries(values).map(([key, value]) => [key, orNull(value)])
        ),
      },
    })
  )

  return (
    <form onSubmit={submit}>
      <DetailSection
        title="Carrier Details"
        description="Corrections are written to the carrier's audit trail against your account. The DOT number cannot be changed here — it is the key the whole directory is joined on, so replacing it is a carrier merge rather than a correction."
        columns={3}
        actions={
          <div className="flex items-center gap-2">
            <DNButton variant="transparent" onClick={onClose} type="button">
              Cancel
            </DNButton>
            <DNButton variant="primary" type="submit" loading={loading}>
              Save
            </DNButton>
          </div>
        }
      >
        {/*
          The DOT number is the key the whole directory is joined on, so it is
          shown but never editable — changing it is a carrier merge, not a
          correction.
        */}
        {/*
          No `description` on this one: it would add a line under the label and
          push this input out of line with the two beside it. The section's own
          note says why it is locked.
        */}
        <TextInput
          label="DOT number"
          value={carrier.dotNumber ?? ''}
          disabled
        />
        <TextInput label="DBA name" {...form.getInputProps('dbaName')} />
        <TextInput label="Legal name" {...form.getInputProps('legalName')} />

        <TextInput label="Phone" {...form.getInputProps('phoneNumber')} />
        <TextInput label="Fax" {...form.getInputProps('faxNumber')} />
        <TextInput label="Email" {...form.getInputProps('email')} />

        <TextInput label="Website" {...form.getInputProps('website')} />
        <TextInput label="Street" {...form.getInputProps('physicalStreet')} />
        <TextInput label="City" {...form.getInputProps('physicalCity')} />

        <TextInput label="State" {...form.getInputProps('physicalState')} />
        <TextInput label="ZIP" {...form.getInputProps('physicalZip')} />
        <TextInput label="Country" {...form.getInputProps('physicalCountry')} />
      </DetailSection>
    </form>
  )
}

export default CarrierEditForm
